import { useCallback, useContext, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "@emotion/styled";
import { CartGroupItem, ScrollableContainer, ViewportContext } from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { getMonth, isLastYearMonth, valueExists } from "@monorepo/helpers";
import { useHistoricalPayment } from "@monorepo/hooks";
import { FolderIcon } from "@monorepo/icons";
import { useGetCartGroupsQuery } from "@monorepo/services/api/carts";
import { initialAttributesState } from "@monorepo/slices/attributes";
import theme, { iconsSizes } from "@monorepo/styles/theme";
import { IActiveCartSliceState, IAttributesSliceState } from "@monorepo/types";
import { AddCircleOutline } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { reverse } from "named-urls";

interface IProps {
  activeCartGroupId?: string;
  activeCartId?: string;
}

export const StyledScrollableContainer = styled(ScrollableContainer)(() => ({
  [theme.breakpoints.down("md")]: {
    order: 3,
  },
}));

export const CartsList = ({ activeCartId, activeCartGroupId }: IProps) => {
  const { data: cartGroups } = useGetCartGroupsQuery();
  const { isHistorical } = useHistoricalPayment();
  const storedActiveCartId = useSelector(({ activeCartSlice }: IActiveCartSliceState) => activeCartSlice.cartId);
  const history = useHistory();
  const { t } = useTranslation();
  const { isThirdParty, isMobile } = useContext(ViewportContext);
  const { canceled, orderId } =
    useSelector(({ attributesSlice }: IAttributesSliceState) => attributesSlice?.["attributes-object"]) ||
    initialAttributesState["attributes-object"];

  useEffect(() => {
    if (activeCartGroupId || !cartGroups || storedActiveCartId || (isThirdParty && (isMobile || canceled || orderId))) {
      return;
    }
    history.push(
      reverse(routeConfig.dashboard.payments.cartGroup.self, {
        cartGroupId: cartGroups[0].id,
      })
    );
  }, [activeCartGroupId, cartGroups, history, storedActiveCartId, isThirdParty, isMobile, canceled, orderId]);

  const listRef = useRef<HTMLDivElement>(null);
  const makeRef = useCallback(
    (id: string, node: HTMLDivElement | null) => {
      if (id === String(activeCartGroupId) && node && !isThirdParty) {
        if (
          Math.abs(
            Number(listRef.current?.getBoundingClientRect().bottom) - Number(node.getBoundingClientRect().top)
          ) <= 25.0
        ) {
          // cart group item is barely visible, scroll into view
          node.scrollIntoView();
        }
      }
    },
    [activeCartGroupId, isThirdParty]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: theme.spacing(2),
        height: "100%",
        overflow: "hidden",
        [theme.breakpoints.down("md")]: {
          height: "auto",
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridAutoFlow: "column",
          alignSelf: "center",
          justifyContent: "center",
          color: theme.palette.text.disabled,
          textTransform: "capitalize",
          [theme.breakpoints.down("md")]: {
            order: 2,
          },
        }}
      >
        {!isLastYearMonth(isHistorical) && new Date().getFullYear()} {t(getMonth(isHistorical))}
      </Box>
      <StyledScrollableContainer ref={listRef}>
        <Box
          sx={{
            display: "grid",
            gridRowGap: theme.spacing(2),
            padding: `0px ${theme.spacing(2)} ${theme.spacing(2)} 0px`,
            [theme.breakpoints.down("md")]: {
              height: "auto",
              padding: 0,
            },
          }}
        >
          {cartGroups &&
            cartGroups.map((cartGroup, index) => {
              const path = reverse(routeConfig.dashboard.payments.cartGroup.self, {
                cartGroupId: cartGroup.id,
              });
              return (
                <Box
                  key={cartGroup.id}
                  ref={(el) => makeRef(cartGroup.id, el as HTMLDivElement)}
                  paddingBottom={{ xs: index === cartGroups.length - 1 ? "3rem" : "0", md: "0" }}
                >
                  <CartGroupItem
                    path={path}
                    cartGroupId={cartGroup.id}
                    activeCartGroupId={activeCartGroupId || storedActiveCartId}
                    activeCartId={activeCartId}
                    title={cartGroup.name}
                  />
                </Box>
              );
            })}
        </Box>
      </StyledScrollableContainer>
      <Button
        sx={{
          color: theme.palette.text.disabled,
          mb: theme.spacing(1),
          [theme.breakpoints.down("md")]: {
            order: 1,
          },
        }}
        variant="contained"
        color="secondary"
        disableElevation
        onClick={() => history.push(routeConfig.dashboard.payments.cartGroup.createCartGroup)}
        startIcon={<FolderIcon sx={{ stroke: theme.palette.gullGray.main, height: 30, width: 30 }} />}
      >
        {t("payments.create_new_payment_list")}
      </Button>
      {!isThirdParty && valueExists(activeCartGroupId) ? (
        <Button
          sx={{
            color: theme.palette.text.disabled,
            mb: theme.spacing(1),
            [theme.breakpoints.down("md")]: {
              order: 1,
            },
          }}
          variant="contained"
          color="secondary"
          disableElevation
          onClick={() => {
            if (!valueExists(activeCartGroupId)) {
              return;
            }
            ReactGA.event("cart_share");
            history.push(
              reverse(routeConfig.dashboard.payments.cartGroup.shareCartGroup, {
                cartGroupId: activeCartGroupId,
              })
            );
          }}
          startIcon={<AddCircleOutline sx={{ width: 30, height: 30, fontSize: iconsSizes.small }} />}
        >
          {t("payments.invite_new_payer")}
        </Button>
      ) : null}
    </Box>
  );
};

export default CartsList;
